import React from 'react';
import { v4 as uuid } from 'uuid';

import { Button } from '@xing-com/button';
import type { SocialTrackingMetadataInput } from '@xing-com/crate-common-graphql-types';
import {
  ShareSuccessBanner,
  useSocialBanner,
} from '@xing-com/crate-social-common-share';
import { useWrapRedirectLoggedOutUserOnClick } from '@xing-com/crate-social-utils';
import { IconShare } from '@xing-com/icons';
import { ModalPage } from '@xing-com/modal-page';

import { ShareTabView } from '../share-tab-view/share-tab-view';

export type ShareTabOmViewType = {
  backendTrackingMetaData?: SocialTrackingMetadataInput;
  buttonClassName?: string;
  entryPoint?: string;
  interactionTargetUrn?: string;
  onButtonClick?: () => void;
  onCancel?: () => void;
  onError?: () => void;
  onSuccess?: () => void;
  propChannel?: string;
  propSourceType?: string;
  trigger?: (triggerModal: () => void) => JSX.Element;
  url: string;
  verb?: 'SHARE' | 'POST';
};

export const ShareTabOmView: React.FC<ShareTabOmViewType> = ({
  backendTrackingMetaData = { trackingTokens: [], page: '', position: '' },
  buttonClassName,
  entryPoint = '',
  interactionTargetUrn = '',
  onButtonClick = () => undefined,
  onCancel = () => undefined,
  onError = () => undefined,
  onSuccess = () => undefined,
  propChannel = '',
  propSourceType = '',
  trigger,
  url,
  verb = 'POST',
}) => {
  const propSocialInteractionId = React.useRef(uuid()).current;
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const { bannerProps, setBannerProps } = useSocialBanner();

  const [showModal, setShowModal] = React.useState(false);

  const { handleClick: handleRedirectLoggedOutUser } =
    useWrapRedirectLoggedOutUserOnClick();

  const onTriggerClick = (): void => {
    setBannerProps({ show: false });
    onButtonClick();
    handleRedirectLoggedOutUser();
    setShowModal(true);
  };

  return (
    <>
      {trigger ? (
        trigger(() => setShowModal(true))
      ) : (
        <Button
          innerRef={buttonRef}
          onClick={() => onTriggerClick()}
          icon={IconShare}
          className={buttonClassName}
        />
      )}
      {showModal ? (
        <ModalPage
          isShown
          closeButton={{
            onClick: () => {
              buttonRef?.current?.focus();
              onCancel();
            },
          }}
          isAnimated
          noGrid
        >
          <ShareTabView
            {...{
              backendTrackingMetaData,
              entryPoint,
              interactionTargetUrn,
              onButtonClick,
              onError,
              propChannel,
              propSocialInteractionId,
              propSourceType,
              url,
              verb,
            }}
            onCancel={() => {
              onCancel();
              setShowModal(false);
            }}
            onSuccess={({ redirectUrl, targetType }) => {
              setBannerProps({ redirectUrl, show: true, targetType });
              onSuccess();
              setShowModal(false);
            }}
          />
        </ModalPage>
      ) : null}
      <ShareSuccessBanner {...{ ...bannerProps, setBannerProps }} />
    </>
  );
};
